import React, { useState, useEffect } from "react";
import { useNavigate, Link, Outlet, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Check,
  Sparkles,
} from "lucide-react";

// Progress indicator component
const ProgressSteps = ({ currentStep, totalSteps = 4 }) => {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {Array.from({ length: totalSteps }, (_, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === currentStep;
        const isCompleted = stepNum < currentStep;
        
        return (
          <React.Fragment key={stepNum}>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                isCompleted
                  ? "bg-green-500 text-white"
                  : isActive
                  ? "bg-[#C8902A] text-white ring-4 ring-[#C8902A]/20"
                  : "bg-[#E5D4B3] text-[#4A2C0A]/50"
              }`}
            >
              {isCompleted ? <Check className="w-4 h-4" /> : stepNum}
            </div>
            {stepNum < totalSteps && (
              <div
                className={`w-12 h-1 rounded-full transition-all duration-300 ${
                  isCompleted ? "bg-green-500" : "bg-[#E5D4B3]"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Layout wrapper for the registration flow
export default function RegistroVendedorLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine current step based on route
  const getStep = () => {
    const path = location.pathname;
    if (path.includes("/acceso")) return 1;
    if (path.includes("/tipo")) return 2;
    if (path.includes("/datos")) return 3;
    if (path.includes("/espera")) return 4;
    return 0; // Landing
  };
  
  const currentStep = getStep();
  const showProgress = currentStep > 0;
  const isLanding = currentStep === 0;

  // Landing page has its own full-screen layout
  if (isLanding) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FBF5E8] via-[#F5EBD7] to-[#EDD9C0]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E5D4B3]">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/logo-torta-cuajada.png"
              alt="Torta de Cuajada"
              className="w-10 h-10 rounded-full object-cover ring-2 ring-[#C8902A]/20 group-hover:ring-[#C8902A]/40 transition-all"
            />
            <div className="hidden sm:block">
              <p className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold">Torta de Cuajada</p>
              <p className="text-xs text-[#4A2C0A]/60 font-['Poppins']">Únete al equipo</p>
            </div>
          </Link>
          
          {currentStep > 0 && currentStep < 4 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-[#4A2C0A]/70 hover:text-[#4A2C0A]"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Atrás
            </Button>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="pt-24 pb-12 px-4 min-h-screen">
        <div className="max-w-2xl mx-auto">
          {showProgress && <ProgressSteps currentStep={currentStep} />}
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white/60 backdrop-blur-sm border-t border-[#E5D4B3] py-3">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-center gap-2 text-xs text-[#4A2C0A]/60 font-['Poppins']">
          <Sparkles className="w-3 h-3 text-[#C8902A]" />
          <span>Empieza a ganar hoy — es gratis</span>
        </div>
      </footer>
    </div>
  );
}
